import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  Chip,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore — module ships JS at .esm.js, types only cover the named export
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  getPresenterView,
  PresenterView as PresenterViewData,
  PresenterEntry,
  PresenterSection,
  CodeSubmissionEntry,
} from '../services/api';

function parseLabel(checkpointId: string): string {
  const sep = Math.max(checkpointId.lastIndexOf('.'), checkpointId.lastIndexOf('/'));
  if (sep <= 0 || sep >= checkpointId.length - 1) return checkpointId;
  return checkpointId.substring(sep + 1);
}

// Minimal markdown: ATX headings (#..######) become bold headings; blank lines
// separate paragraphs. Deliberately tiny — no markdown dependency.
function MiniMarkdown({ text, dense }: { text: string; dense?: boolean }) {
  const blocks = text.split(/\n{2,}/);
  return (
    <Box sx={{ mb: dense ? 0 : 3 }}>
      {blocks.map((block, i) => {
        const m = /^\s{0,3}(#{1,6})\s+(.*)$/.exec(block.trim());
        if (m) {
          const level = m[1].length;
          return (
            <Typography
              key={i}
              variant={level <= 1 ? 'h4' : level === 2 ? 'h5' : 'h6'}
              sx={{ fontWeight: 700, mt: i ? 1 : 0, mb: 1 }}
            >
              {m[2]}
            </Typography>
          );
        }
        return (
          <Typography key={i} variant="body1" sx={{ mb: 1.25, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {block}
          </Typography>
        );
      })}
    </Box>
  );
}

function SubmissionBlock({ sub, index }: { sub: CodeSubmissionEntry; index: number }) {
  const hasImage = !!sub.image_data_b64;
  const hasCode = !!sub.code;
  const hasNote = !!sub.note;
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Chip size="small" label={`${index + 1}`} sx={{ height: 24, fontWeight: 700 }} />
        <Typography
          component="code"
          sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1rem', fontWeight: 600 }}
        >
          {parseLabel(sub.checkpoint_id)}
        </Typography>
        {sub.is_kept && (
          <Chip
            size="small"
            icon={<StarIcon sx={{ fontSize: '0.9rem !important', color: '#f59e0b !important' }} />}
            label="kept"
            sx={{ height: 22, backgroundColor: '#fef3c7', color: '#92400e' }}
          />
        )}
        {sub.display_name && (
          <Typography variant="caption" color="text.secondary">
            · {sub.display_name}
          </Typography>
        )}
      </Box>
      {hasNote && (
        <Box sx={{ mb: 2, p: 2, borderRadius: 1.5, backgroundColor: '#f8fafc', borderLeft: '3px solid', borderColor: 'primary.light' }}>
          <MiniMarkdown text={sub.note as string} dense />
        </Box>
      )}
      {hasImage && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 1.5,
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            mb: hasCode ? 2 : 0,
          }}
        >
          <img
            src={`data:${sub.image_mime || 'image/png'};base64,${sub.image_data_b64}`}
            alt={`Submission for ${sub.checkpoint_id}`}
            style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 6, display: 'block' }}
          />
        </Box>
      )}
      {hasCode && (
        <Box sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
          <SyntaxHighlighter
            language={sub.language || 'python'}
            style={vscDarkPlus}
            // Sized for projecting to a room — large, high-contrast, roomy.
            customStyle={{ margin: 0, fontSize: '1.35rem', padding: 28, lineHeight: 1.75 }}
            codeTagProps={{ style: { fontFamily: '"JetBrains Mono", monospace' } }}
            wrapLongLines
          >
            {sub.code as string}
          </SyntaxHighlighter>
        </Box>
      )}
    </Box>
  );
}

function PresenterStage({ entry, sections }: { entry: PresenterEntry; sections: PresenterSection[] }) {
  // Slot this presenter's submissions under the lesson's section headings. All
  // sections show (even empty ones) so the teacher sees the full structure.
  let slide = 0;
  const inSection = (idx: number | null) =>
    entry.submissions.filter((s) => (s.section_index ?? null) === idx);
  const ungrouped = inSection(null); // submitted before the notebook's first heading

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {entry.kind === 'team' ? <GroupsIcon color="primary" /> : <PersonIcon color="action" />}
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {entry.presentation?.title || entry.display_name}
        </Typography>
      </Box>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        {entry.kind === 'team' ? 'Team' : 'Student'}: {entry.display_name}
        {entry.members.length > 0 && ` · ${entry.members.join(', ')}`}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {entry.presentation?.narrative && <MiniMarkdown text={entry.presentation.narrative} />}

      {ungrouped.map((sub) => <SubmissionBlock key={sub.id} sub={sub} index={slide++} />)}

      {sections.map((sec) => {
        const subs = inSection(sec.index);
        return (
          <Box key={sec.index} sx={{ mb: 3 }}>
            <Box sx={{ borderBottom: '2px solid', borderColor: 'divider', mb: 2, pb: 0.5 }}>
              <MiniMarkdown text={sec.heading_md} dense />
            </Box>
            {subs.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mb: 2 }}>
                No submission under this section.
              </Typography>
            ) : (
              subs.map((sub) => <SubmissionBlock key={sub.id} sub={sub} index={slide++} />)
            )}
          </Box>
        );
      })}

      {sections.length === 0 && ungrouped.length === 0 && (
        <Typography color="text.secondary">No submissions to present yet.</Typography>
      )}
    </Box>
  );
}

export default function PresenterMode() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [data, setData] = useState<PresenterViewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('No teacher token in the URL.');
      return;
    }
    let cancelled = false;
    getPresenterView(token)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (d.presenters.length) setSelectedId(d.presenters[0].id);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const selected = data?.presenters.find((p) => p.id === selectedId) || null;

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
        <Button component={RouterLink} to={`/teacher/live?token=${encodeURIComponent(token)}`} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to dashboard
        </Button>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} /> <Typography>Loading presentations…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 140px)', gap: 0 }}>
      {/* Presenter picker */}
      <Box sx={{ width: 280, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto' }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Button
            component={RouterLink}
            to={`/teacher/live?token=${encodeURIComponent(token)}`}
            startIcon={<ArrowBackIcon />}
            size="small"
            sx={{ mb: 1 }}
          >
            Dashboard
          </Button>
          <Typography variant="h6">Presenting</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {data.lesson_name} · {data.presenters.length} presenter{data.presenters.length === 1 ? '' : 's'}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {data.presentation_mode === 'marked' ? 'showing marked work' : 'showing all submitted work'}
          </Typography>
        </Box>
        <List dense>
          {data.presenters.map((p) => (
            <ListItemButton
              key={p.id}
              selected={p.id === selectedId}
              onClick={() => setSelectedId(p.id)}
              sx={{ gap: 1 }}
            >
              {p.kind === 'team' ? <GroupsIcon fontSize="small" color="primary" /> : <PersonIcon fontSize="small" color="action" />}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                  {p.presentation?.title || p.display_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {p.submissions.length} kept
                  {p.presentation && ' · curated'}
                </Typography>
              </Box>
            </ListItemButton>
          ))}
        </List>
      </Box>
      {/* Stage */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3 }}>
        {selected ? (
          <PresenterStage entry={selected} sections={data.sections} />
        ) : (
          <Box sx={{ p: 4 }}>
            <Typography color="text.secondary">
              No submissions yet. Once students submit plots or code (and optionally call{' '}
              <code>present(...)</code>), they'll appear here to walk through.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
